'use client'

// React and Next.js imports
import React, { useEffect, useState } from 'react'
import { useSearchParams } from 'next/navigation'

// Third-party library imports
import { Message } from '@locmod/intl'
import { openModal } from '@locmod/modal'
import { useBaseBetslip } from '@azuro-org/sdk'
import cx from 'classnames'

// Local component imports
import Betslip from 'compositions/Betslip/Betslip'
import AcceptedBets from 'compositions/AcceptedBets/AcceptedBets'
import OvertimeBetslip from '../Betslip/OvertimeBetslip'

// Local utility and hook imports
import { useTicket } from '@/contexts/TicketContext'
import useChainProvider from '@/hooks/useChainProvider'
import decompressData from 'src/utils/decompressData'
import decompressOvertimeData from 'src/utils/decompressOvertimeData'
import logger from '@/utils/logger'

// Local file imports
import messages from './messages'

const tabs = ['betslip', 'myBets'] as const

// Function to detect if data is Overtime data
const isOvertimeData = (data: string): boolean => {
  try {
    // Try to decompress as Overtime data first
    const decompressed = decompressOvertimeData(data)

    // If decompression succeeds and has expected structure, it's Overtime data
    if (Array.isArray(decompressed)) {
      return (
        decompressed.length > 0 && decompressed[0].hasOwnProperty('chainId')
      )
    } else {
      return decompressed?.hasOwnProperty('chainId')
    }
  } catch (error) {
    return false
  }
}

const TabbedBetslip: React.FC = () => {
  const [activeTab, setActiveTab] = useState<(typeof tabs)[number]>('betslip')
  const [isProcessingData, setIsProcessingData] = useState(false)
  const searchParams = useSearchParams()
  const { addItem, clear } = useBaseBetslip()
  const { updateTicket, removeAll } = useTicket()

  useEffect(() => {
    const data = searchParams.get('data')
    if (data) {
      sessionStorage.setItem('data', data)

      // Clean up URL without page reload
      window.history.replaceState({}, '', '/')
    }
  }, [searchParams])

  // Second useEffect: Process data from sessionStorage when at root URL
  useEffect(() => {
    const processStoredData = async () => {
      const data = sessionStorage.getItem('data')
      if (data) {
        try {
          setIsProcessingData(true)
          const isOvertime = isOvertimeData(data)

          if (isOvertime) {
            // Handle Overtime data
            const decompressedData = decompressOvertimeData(data)

            // Clear existing betslip
            removeAll()

            // Add a short delay to ensure clearing is complete
            await new Promise(resolve => setTimeout(resolve, 100))

            // Process all items at once without delays to prevent flickering
            if (Array.isArray(decompressedData)) {
              // For combo bets, add all items in quick succession
              for (const item of decompressedData) {
                updateTicket(item)
                // Small delay to ensure proper state updates
                await new Promise(resolve => setTimeout(resolve, 50))
              }
            } else {
              updateTicket(decompressedData)
            }

            sessionStorage.removeItem('data')
            setActiveTab('betslip')
            setIsProcessingData(false)
          } else {
            // Handle Azuro data (existing logic)
            const decompressedData = decompressData(data)

            // Clear existing betslip
            clear()

            // Add a short delay to ensure clearing is complete
            await new Promise(resolve => setTimeout(resolve, 100))

            // Process all items at once without delays to prevent flickering
            if (Array.isArray(decompressedData)) {
              // For combo bets, add all items in quick succession
              for (const item of decompressedData) {
                addItem({
                  conditionId: item.conditionId,
                  outcomeId: item.outcomeId,
                  isExpressForbidden: item.isExpressForbidden,
                  marketName: item.marketName,
                  game: item.game,
                  selectionName: item.selectionName,
                  gameId: item.game.gameId,
                })
                // Small delay to ensure proper state updates
                await new Promise(resolve => setTimeout(resolve, 50))
              }
            } else {
              addItem({
                conditionId: decompressedData.conditionId,
                outcomeId: decompressedData.outcomeId,
                isExpressForbidden: decompressedData.isExpressForbidden,
                marketName: decompressedData.marketName,
                game: decompressedData.game,
                selectionName: decompressedData.selectionName,
                gameId: decompressedData.game.gameId,
              })
            }

            sessionStorage.removeItem('data')
            setActiveTab('betslip')

            // Check if betslip items were added successfully
            const betslipItems = localStorage.getItem('betslipItems-v3') || '[]'
            if (betslipItems === '[]') {
              openModal('ErrorModal', {
                title: 'Copybet Error',
                text: 'Failed to copy the bet.',
              })
            }
            setIsProcessingData(false)
          }
        } catch (error) {
          logger.error('Error processing stored data:', error)
          sessionStorage.removeItem('data')
          setIsProcessingData(false)
        }
      }
    }

    processStoredData()
  }, [addItem, clear, updateTicket, removeAll]) // Only run once when component mounts and when betslip functions are available

  const { isOvertimeChain, isAzuroChain } = useChainProvider()

  return (
    <>
      <div className="bg-accent-grey-980 rounded-md border border-accent-grey-800 p-1 flex items-center">
        {tabs.map(tab => {
          const isActive = activeTab === tab

          return (
            <button
              key={tab}
              className={cx('w-full p-2 text-center rounded-sm', {
                'text-accent-grey-200 hover:text-accent-grey-30': !isActive,
                'text-accent-grey-30 bg-accent-grey-950': isActive,
              })}
              onClick={() => setActiveTab(tab)}
            >
              <Message value={messages[tab]} />
            </button>
          )
        })}
      </div>
      {(() => {
        switch (activeTab) {
          case 'betslip': {
            if (isProcessingData) {
              return (
                <div className="flex justify-center items-center p-8">
                  <div className="text-accent-grey-300">
                    Processing bet data...
                  </div>
                </div>
              )
            }
            switch (true) {
              case isOvertimeChain: {
                return <OvertimeBetslip />
              }
              case isAzuroChain: {
                return <Betslip />
              }
              default: {
                return <Betslip />
              }
            }
          }
          case 'myBets': {
            return <AcceptedBets />
          }
          default: {
            return null
          }
        }
      })()}
    </>
  )
}

export default TabbedBetslip
